import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MusicPlayer = () => {
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const [arrancaConSobre, setArrancaConSobre] = useState(false);
  const [pulsoInicial, setPulsoInicial] = useState(true); // v73.50 — admin puede desactivar la animación
  // v73.51 — posición/tamaño del botón
  const [botonEsquina, setBotonEsquina] = useState('bottom-right');
  const [botonOffsetX, setBotonOffsetX] = useState(0);
  const [botonOffsetY, setBotonOffsetY] = useState(0);
  const [botonTamano, setBotonTamano] = useState(56);
  const [musicUrl, setMusicUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // v73.40 — Pulso visual en el botón hasta que la música arranca (5s) o el usuario toca
  const [pulseUntilStart, setPulseUntilStart] = useState(true);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchMusicConfig();
  }, []);

  const fetchMusicConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/musica/config`);
      if (response.ok) {
        const data = await response.json();
        setMusicEnabled(data.musica_habilitada === true);
        setShowButton(data.mostrar_boton_musica === true);
        setAutoplay(data.musica_autoplay !== false);
        setArrancaConSobre(data.musica_arranca_con_sobre === true);
        setPulsoInicial(data.musica_pulso_inicial !== false); // v73.50
        // v73.51
        setBotonEsquina(data.musica_boton_esquina || 'bottom-right');
        setBotonOffsetX(Number(data.musica_boton_offset_x) || 0);
        setBotonOffsetY(Number(data.musica_boton_offset_y) || 0);
        setBotonTamano(Number(data.musica_boton_tamano) || 56);
        if (data.archivo_musica) {
          setMusicUrl(`${API_URL}${data.archivo_musica}`);
        }
      }
    } catch (error) {
      console.error('Error fetching music config:', error);
    }
  };

  // Sincronizar estado isPlaying con el audio real
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => { setIsPlaying(true); setPulseUntilStart(false); };
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [musicUrl]);

  // v73.40 — playMusic con bypass muted para Android Chrome/Opera/Samsung Browser.
  // Estrategia:
  //   1. Intentar play() normal.
  //   2. Si Android lo bloquea ("NotAllowedError"), reintentar con audio.muted=true
  //      (Chromium permite autoplay silenciado siempre), y a los 50ms quitar el mute.
  //      El usuario no nota el "silencio" porque dura una fracción de segundo.
  const playMusic = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = false;
    const tryPlay = audio.play();
    if (!tryPlay || typeof tryPlay.then !== 'function') {
      setHasUserInteracted(true);
      return;
    }
    tryPlay
      .then(() => { setHasUserInteracted(true); })
      .catch(() => {
        // Bypass muted (Android user gesture limitations)
        audio.muted = true;
        audio.play()
          .then(() => {
            setHasUserInteracted(true);
            setTimeout(() => {
              try { if (audioRef.current) audioRef.current.muted = false; } catch { /* ignore */ }
            }, 50);
          })
          .catch((err) => {
            console.log('Play bloqueado incluso con bypass muted:', err);
          });
      });
  }, []);

  const pauseMusic = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
  }, []);

  // Listener para la primera interacción global (click/touch en cualquier sitio).
  // Si `arrancaConSobre=ON`, este listener se DESACTIVA y la música solo arranca
  // al pulsar el sobre de la intro (lo gestiona el siguiente useEffect).
  useEffect(() => {
    if (!autoplay || !musicEnabled || !musicUrl || hasUserInteracted) return;
    if (arrancaConSobre) return;

    const handleFirstInteraction = (e) => {
      const target = e.target;
      const musicButton = target.closest && target.closest('[data-testid="music-control-button"]');
      if (musicButton) return;
      playMusic();
    };

    document.addEventListener('click', handleFirstInteraction, { capture: true });
    document.addEventListener('touchstart', handleFirstInteraction, { capture: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction, { capture: true });
      document.removeEventListener('touchstart', handleFirstInteraction, { capture: true });
    };
  }, [autoplay, musicEnabled, musicUrl, hasUserInteracted, arrancaConSobre, playMusic]);

  // v73.40 — Listener del evento global "intro-envelope-clicked".
  // El sobre de la intro lo dispara síncronamente desde su onClick.
  // Aquí estamos GARANTIZADOS dentro de la ventana del user gesture (gesto trusted)
  // → ideal para Android.
  useEffect(() => {
    if (!autoplay || !musicEnabled || !musicUrl) return;
    if (!arrancaConSobre) return;
    const onEnvelope = () => { if (!hasUserInteracted) playMusic(); };
    window.addEventListener('intro-envelope-clicked', onEnvelope);
    return () => window.removeEventListener('intro-envelope-clicked', onEnvelope);
  }, [autoplay, musicEnabled, musicUrl, arrancaConSobre, hasUserInteracted, playMusic]);

  // v73.40 — Apagar el pulso del botón a los 5s si todavía no ha sonado.
  useEffect(() => {
    if (!pulseUntilStart) return;
    const t = setTimeout(() => setPulseUntilStart(false), 5000);
    return () => clearTimeout(t);
  }, [pulseUntilStart]);

  const handleToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!audioRef.current) return;
    if (audioRef.current.paused) playMusic();
    else pauseMusic();
  };

  if (!musicEnabled || !musicUrl) return null;

  // Pulse SOLO si: admin lo ha permitido, autoplay ON, aún no ha sonado, botón visible, y dentro de los 5s
  const showPulse = pulsoInicial && autoplay && !hasUserInteracted && showButton && pulseUntilStart;

  return (
    <>
      <audio
        ref={audioRef}
        src={musicUrl}
        loop
        preload="auto"
        playsInline
      />
      {showButton && (() => {
        // v73.51/v73.59 — calcular estilos según esquina/offset/tamaño configurados
        const isTop = botonEsquina.startsWith('top');
        const isLeft = botonEsquina.endsWith('left');
        // Bases conservadoras: 16 px horizontal, 16/80 px vertical para no chocar
        // con otros botones flotantes (whatsapp inferior derecho).
        const baseV = isTop ? 16 : 80;
        const baseH = 16;
        // v73.59 — el offset puede ser NEGATIVO (Admin permite valores < 0
        // para acercar el botón hasta el borde de la pantalla). Garantizamos
        // distancia mínima de 0 para que el botón nunca quede fuera del viewport.
        const distV = Math.max(0, baseV + (botonOffsetY || 0));
        const distH = Math.max(0, baseH + (botonOffsetX || 0));
        const posStyle = {
          position: 'fixed',
          [isTop ? 'top' : 'bottom']: `${distV}px`,
          [isLeft ? 'left' : 'right']: `${distH}px`,
          zIndex: 9999,
          width: `${botonTamano}px`,
          height: `${botonTamano}px`,
          padding: 0,
          backgroundColor: isPlaying ? '#D4846D' : '#5C5C5C',
          color: 'white',
          WebkitTapHighlightColor: 'transparent',
        };
        const iconSize = Math.max(16, Math.floor(botonTamano * 0.45));
        return (
          <button
            type="button"
            onClick={handleToggleClick}
            onTouchEnd={handleToggleClick}
            className={`rounded-full shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center ${showPulse ? 'animate-pulse' : ''}`}
            style={posStyle}
            title={isPlaying ? 'Pausar música' : 'Reproducir música'}
            data-testid="music-control-button"
          >
            {isPlaying ? (
              <Volume2 style={{ width: iconSize, height: iconSize }} className="pointer-events-none" />
            ) : (
              <VolumeX style={{ width: iconSize, height: iconSize }} className="pointer-events-none" />
            )}
          </button>
        );
      })()}
    </>
  );
};

export default MusicPlayer;
